import * as Blockly from 'blockly';
import { spikeTheme } from './theme/spike-theme.js';
import { registerRetoCarBlocks } from './blocks/retocar-blocks.js';
import { registerPythonGenerators, generateRetoCarPythonCode } from './generators/python-generator.js';
import { spikeToolbox } from './toolbox.js';
import { applyVietnameseLocalization } from './localization/vietnamese-msg.js';
import { registerToolboxCategoryIcons, applyCategoryIconsToDom } from './category-icons.js';
import { MyBlocksModal } from '../ui/my-blocks-modal.js';
import { VariableModal } from '../ui/variable-modal.js';

// Đảm bảo đăng ký từ điển tiếng Việt, khối và generator một lần duy nhất
let isBlocksRegistered = false;
function ensureRegistered() {
  if (!isBlocksRegistered) {
    applyVietnameseLocalization(Blockly);
    registerRetoCarBlocks();
    registerPythonGenerators();
    registerToolboxCategoryIcons(Blockly);

    // Vô hiệu hóa việc tạo checkbox dấu tick (checkmark) trên các dropdown item
    if (Blockly.MenuItem && Blockly.MenuItem.prototype) {
      Blockly.MenuItem.prototype.toggleHasCheckbox = function () {};
    }

    // Cấu hình kích thước chuẩn cho nút bấm Flyout (Tạo một biến số..., Tạo một danh sách...)
    if (Blockly.FlyoutButton) {
      Blockly.FlyoutButton.TEXT_MARGIN_X = 20;
      Blockly.FlyoutButton.TEXT_MARGIN_Y = 11;
      Blockly.FlyoutButton.BORDER_RADIUS = 8;
    }

    // Gắn CSS class (type và styleName) cho SVG root của mỗi khối lệnh để hỗ trợ styling chuyên biệt
    if (Blockly.BlockSvg && !Blockly.BlockSvg.prototype._retoInjected) {
      Blockly.BlockSvg.prototype._retoInjected = true;
      const origInitSvg = Blockly.BlockSvg.prototype.initSvg;
      Blockly.BlockSvg.prototype.initSvg = function () {
        origInitSvg.call(this);
        if (this.svgGroup_) {
          if (this.type) this.svgGroup_.classList.add(this.type);
          const style = this.getStyleName && this.getStyleName();
          if (style) this.svgGroup_.classList.add(style);
        }
      };
    }

    isBlocksRegistered = true;
  }
}

export class BlocklyWorkspace {
  constructor(options) {
    this.container = options.container;
    this.projectManager = options.projectManager;
    this.onBlocksChange = options.onBlocksChange;
    this.myBlocksModal = null;
    this.variableModal = null;

    ensureRegistered();
    this.init();
  }

  init() {
    // Tạo phần tử mount cho Blockly bên trong container
    this.container.innerHTML = `
      <div id="blockly-mount-surface" style="width: 100%; height: 100%; position: relative;"></div>
    `;

    const mountEl = document.getElementById('blockly-mount-surface');

    // Khởi tạo Google Blockly Workspace với cấu hình chuẩn LEGO SPIKE
    this.workspace = Blockly.inject(mountEl, {
      toolbox: spikeToolbox,
      renderer: 'zelos', // CHUẨN LEGO SPIKE PRIME & SCRATCH 3.0
      theme: spikeTheme,
      zoom: {
        controls: true,
        wheel: true,
        startScale: 0.85,
        maxScale: 2.0,
        minScale: 0.35,
        scaleSpeed: 1.1
      },
      grid: {
        spacing: 25,
        length: 3,
        colour: '#e0e0e0',
        snap: true
      },
      trashcan: true,
      move: {
        scrollbars: true,
        drag: true,
        wheel: true
      }
    });

    // Áp dụng icon cho danh mục nhóm lệnh và quan sát để luôn giữ icon
    applyCategoryIconsToDom(mountEl);
    this.toolboxObserver = new MutationObserver(() => {
      applyCategoryIconsToDom(mountEl);
    });
    this.toolboxObserver.observe(mountEl, { childList: true, subtree: true });

    // Đồng bộ không gian làm việc (Blockly Workspace Offset) với bề rộng Toolbox 148px
    Blockly.svgResize(this.workspace);

    // Khởi tạo Modal "Khối của tôi" chuẩn LEGO SPIKE
    const modalContainer = document.getElementById('my-blocks-modal-container') || document.body;
    this.myBlocksModal = new MyBlocksModal({
      container: modalContainer,
      getWorkspace: () => this.workspace,
      onCreated: (name) => {
        if (this.onBlocksChange) this.onBlocksChange();
      }
    });

    // Khởi tạo Modal Tạo Biến Số & Danh Sách Chuẩn LEGO SPIKE
    const varModalContainer = document.getElementById('variable-modal-container') || document.body;
    this.variableModal = new VariableModal({
      container: varModalContainer,
      onConfirm: (name, type) => {
        if (!this.workspace) return;
        const vmap = this.workspace.getVariableMap();
        if (vmap) {
          const varType = type === 'list' ? 'list' : '';
          const otherType = type === 'list' ? '' : 'list';
          const conflict = vmap.getVariablesOfType(otherType).find(
            v => v.getName().toLowerCase() === name.toLowerCase()
          );
          if (conflict) {
            const otherTypeName = type === 'list' ? 'biến số' : 'danh sách';
            this.variableModal.showError(`Tên "${name}" đã tồn tại cho một ${otherTypeName}. Vui lòng chọn tên khác!`);
            return false;
          }
          const existing = vmap.getVariable(name, varType);
          if (!existing) {
            vmap.createVariable(name, varType);
          }
        }
        if (typeof this.workspace.refreshToolboxSelection === 'function') {
          this.workspace.refreshToolboxSelection();
        }
        const toolbox = this.workspace.getToolbox ? this.workspace.getToolbox() : null;
        if (toolbox && typeof toolbox.refreshSelection === 'function') {
          toolbox.refreshSelection();
        }
        if (this.onBlocksChange) this.onBlocksChange();
      }
    });

    // Đăng ký các Callback cho nút bấm và danh mục động
    this.setupToolboxCallbacks();

    // Tải dữ liệu khối từ projectManager nếu có
    const initialData = this.projectManager?.currentProject?.blocklyData || this.projectManager?.currentProject?.blocksData;
    if (initialData && ((initialData.blocks && initialData.blocks.blocks && initialData.blocks.blocks.length > 0) || (Array.isArray(initialData) && initialData.length > 0))) {
      this.loadBlocksData(initialData);
    } else {
      // Để workspace trống mặc định theo yêu cầu người dùng
      this.workspace.clear();
    }

    // Lắng nghe các thay đổi trên Workspace (kéo, thả, ghép ngàm, đổi giá trị, xóa)
    this.workspace.addChangeListener((event) => {
      // Bỏ qua các sự kiện UI thuần túy (như cuộn chuột, chọn category flyout)
      if (event.isUiEvent) return;

      const currentJson = this.saveBlocksData();
      if (this.projectManager) {
        this.projectManager.updateBlocksData(currentJson);
        this.projectManager.currentProject.blocklyData = currentJson;
        this.projectManager.currentProject.pythonSource = this.getPythonCode();
      }

      if (this.onBlocksChange) {
        this.onBlocksChange();
      }
    });

    // Resize khi cửa sổ thay đổi kích thước
    this.resizeHandler = () => {
      if (this.workspace) {
        Blockly.svgResize(this.workspace);
      }
    };
    window.addEventListener('resize', this.resizeHandler);
    if (typeof requestAnimationFrame !== 'undefined') {
      requestAnimationFrame(this.resizeHandler);
    }
    setTimeout(this.resizeHandler, 50);
    setTimeout(this.resizeHandler, 150);
  }

  setupToolboxCallbacks() {
    if (!this.workspace) return;

    const openVariableModal = () => {
      if (this.variableModal) {
        this.variableModal.show({
          type: 'variable',
          title: 'Tạo Biến Số Mới',
          label: 'Tên biến số mới:'
        });
      }
    };

    const openListModal = () => {
      if (this.variableModal) {
        this.variableModal.show({
          type: 'list',
          title: 'Tạo Danh Sách Mới',
          label: 'Tên danh sách mới:'
        });
      }
    };

    // Tích hợp trực tiếp vào hệ thống Dialog của Blockly (thay thế triệt để window.prompt)
    if (Blockly.dialog && typeof Blockly.dialog.setPrompt === 'function') {
      Blockly.dialog.setPrompt((message, defaultValue, callback) => {
        if (this.variableModal) {
          const isList = !!(message && (message.toLowerCase().includes('danh sách') || message.toLowerCase().includes('list')));
          this.variableModal.show({
            type: isList ? 'list' : 'variable',
            title: message || (isList ? 'Tạo Danh Sách Mới' : 'Tạo Biến Số Mới'),
            label: isList ? 'Tên danh sách mới:' : 'Tên biến số mới:',
            defaultValue: defaultValue || '',
            callback: (text) => {
              // Lưu ý: setPrompt chỉ truyền giá trị nhập về cho caller callback xử lý
              if (callback) callback(text);
            }
          });
        } else {
          if (callback) callback(null);
        }
      });
    }

    // Ghi đè các hàm thao tác biến số & danh sách mặc định của Blockly
    if (Blockly.Variables) {
      Blockly.Variables.createVariableButtonHandler = (button, opt_callback, opt_type) => {
        if (opt_type === 'list') {
          openListModal();
        } else {
          openVariableModal();
        }
      };

      // Tách bạch hoàn toàn Đổi tên danh sách vs Đổi tên biến số
      Blockly.Variables.renameVariable = (workspace, variable, opt_callback) => {
        if (!workspace || !variable) {
          if (opt_callback) opt_callback(null);
          return;
        }
        const isList = variable.getType() === 'list';
        const oldName = variable.getName();
        const title = isList ? 'Đổi Tên Danh Sách' : 'Đổi Tên Biến Số';
        const label = isList ? 'Tên danh sách mới:' : 'Tên biến số mới:';

        if (this.variableModal) {
          this.variableModal.show({
            type: isList ? 'list' : 'variable',
            title: title,
            label: label,
            defaultValue: oldName,
            callback: (newName) => {
              if (!newName || newName.trim() === '' || newName.trim() === oldName) {
                if (opt_callback) opt_callback(null);
                return;
              }
              newName = newName.trim();
              const vmap = workspace.getVariableMap();

              // 1. Kiểm tra nếu trùng tên với kiểu đối lập
              const otherType = isList ? '' : 'list';
              const conflict = vmap.getVariablesOfType(otherType).find(
                v => v.getName().toLowerCase() === newName.toLowerCase()
              );
              if (conflict) {
                const typeName = isList ? 'biến số' : 'danh sách';
                this.variableModal.showError(`Tên "${newName}" đã tồn tại cho một ${typeName}. Vui lòng chọn tên khác!`);
                return false;
              }

              // 2. Thực hiện đổi tên biến/danh sách chuẩn trong VariableMap
              vmap.renameVariable(variable, newName);

              // 3. Làm mới Flyout & Workspace
              if (typeof workspace.refreshToolboxSelection === 'function') {
                workspace.refreshToolboxSelection();
              }
              const toolbox = workspace.getToolbox ? workspace.getToolbox() : null;
              if (toolbox && typeof toolbox.refreshSelection === 'function') {
                toolbox.refreshSelection();
              }
              if (this.onBlocksChange) this.onBlocksChange();
              if (opt_callback) opt_callback(newName);
            }
          });
        } else {
          if (opt_callback) opt_callback(null);
        }
      };

      // Tách bạch hoàn toàn Xóa danh sách vs Xóa biến số
      Blockly.Variables.deleteVariable = (workspace, variable) => {
        if (!workspace || !variable) return;
        const isList = variable.getType() === 'list';
        const varName = variable.getName();
        const uses = Blockly.Variables.getVariableUsesById ? Blockly.Variables.getVariableUsesById(workspace, variable.getId()) : [];

        const doDelete = () => {
          workspace.getVariableMap().deleteVariable(variable);
          if (typeof workspace.refreshToolboxSelection === 'function') {
            workspace.refreshToolboxSelection();
          }
          const toolbox = workspace.getToolbox ? workspace.getToolbox() : null;
          if (toolbox && typeof toolbox.refreshSelection === 'function') {
            toolbox.refreshSelection();
          }
          if (this.onBlocksChange) this.onBlocksChange();
        };

        if (uses.length > 0) {
          const itemType = isList ? 'danh sách' : 'biến số';
          const confirmMsg = `Xóa ${uses.length} khối đang sử dụng ${itemType} "${varName}"?`;
          if (window.confirm(confirmMsg)) {
            doDelete();
          }
        } else {
          doDelete();
        }
      };
    }

    // 1. Callback tạo biến số (dùng Modal đồ họa chuẩn LEGO SPIKE)
    this.workspace.registerButtonCallback('CREATE_VARIABLE', openVariableModal);

    // 2. Callback tạo danh sách (dùng Modal đồ họa chuẩn LEGO SPIKE)
    this.workspace.registerButtonCallback('CREATE_LIST', openListModal);

    // 3. Callback mở Modal "Khối của tôi"
    this.workspace.registerButtonCallback('CREATE_CUSTOM_BLOCK', () => {
      if (this.myBlocksModal) {
        this.myBlocksModal.show();
      }
    });

    // 4. Danh mục Biến số (VARIABLE_CATEGORY)
    const variableCategoryCallback = (ws) => {
      const xmlList = [];

      // Nút Tạo một biến số...
      xmlList.push({
        kind: 'button',
        text: '%{BKY_NEW_VARIABLE}',
        callbackKey: 'CREATE_VARIABLE'
      });

      // Đảm bảo nút bấm trỏ về modal đồ họa
      if (ws.registerButtonCallback) {
        ws.registerButtonCallback('CREATE_VARIABLE', openVariableModal);
      }

      // Tạo trực tiếp danh sách khối thao tác biến số JSON thuần túy
      const variables = ws.getVariableMap().getVariablesOfType('');
      if (variables.length > 0) {
        const latestVar = variables[variables.length - 1];

        // Khối đặt biến với shadow block math_number để người dùng click vào gõ số được
        xmlList.push({
          kind: 'block',
          type: 'variables_set',
          gap: 8,
          fields: {
            VAR: { name: latestVar.getName(), type: '' }
          },
          inputs: {
            VALUE: {
              shadow: {
                type: 'math_number',
                fields: { NUM: 0 }
              }
            }
          }
        });

        // Khối thay đổi biến
        xmlList.push({
          kind: 'block',
          type: 'math_change',
          gap: 20,
          fields: {
            VAR: { name: latestVar.getName(), type: '' }
          },
          inputs: {
            DELTA: {
              shadow: {
                type: 'math_number',
                fields: { NUM: 1 }
              }
            }
          }
        });

        // Khối đọc giá trị (reporter) cho từng biến số
        for (const v of variables) {
          xmlList.push({
            kind: 'block',
            type: 'variables_get',
            gap: 8,
            fields: {
              VAR: { name: v.getName(), type: '' }
            }
          });
        }
      }

      return xmlList;
    };

    // 5. Danh mục Danh sách dữ liệu (LIST_CATEGORY)
    const listCategoryCallback = (ws) => {
      const xmlList = [];

      // Nút Tạo một danh sách...
      xmlList.push({
        kind: 'button',
        text: 'Tạo một danh sách...',
        callbackKey: 'CREATE_LIST'
      });

      // Đảm bảo nút bấm trỏ về modal đồ họa
      if (ws.registerButtonCallback) {
        ws.registerButtonCallback('CREATE_LIST', openListModal);
      }

      // Chỉ khi có danh sách được tạo thì mới hiển thị các khối thao tác danh sách
      const listVars = ws.getVariableMap().getVariablesOfType('list');
      if (listVars.length > 0) {
        const latestList = listVars[listVars.length - 1];

        // Khối đọc danh sách (reporter) cho từng danh sách đã tạo
        for (const l of listVars) {
          xmlList.push({
            kind: 'block',
            type: 'reto_list_reporter',
            gap: 8,
            fields: {
              VAR: { name: l.getName(), type: 'list' }
            }
          });
        }

        // 8 Khối thao tác Danh sách Scratch 100% Tiếng Việt (loại bỏ tạo danh sách rỗng)
        xmlList.push(
          {
            kind: 'block',
            type: 'reto_list_add',
            fields: {
              LIST: { name: latestList.getName(), type: 'list' }
            },
            inputs: {
              ITEM: { shadow: { type: 'math_number', fields: { NUM: 0 } } }
            }
          },
          {
            kind: 'block',
            type: 'reto_list_delete',
            fields: {
              LIST: { name: latestList.getName(), type: 'list' }
            },
            inputs: {
              INDEX: { shadow: { type: 'math_number', fields: { NUM: 1 } } }
            }
          },
          {
            kind: 'block',
            type: 'reto_list_delete_all',
            fields: {
              LIST: { name: latestList.getName(), type: 'list' }
            }
          },
          {
            kind: 'block',
            type: 'reto_list_insert',
            fields: {
              LIST: { name: latestList.getName(), type: 'list' }
            },
            inputs: {
              ITEM: { shadow: { type: 'math_number', fields: { NUM: 0 } } },
              INDEX: { shadow: { type: 'math_number', fields: { NUM: 1 } } }
            }
          },
          {
            kind: 'block',
            type: 'reto_list_replace',
            fields: {
              LIST: { name: latestList.getName(), type: 'list' }
            },
            inputs: {
              INDEX: { shadow: { type: 'math_number', fields: { NUM: 1 } } },
              ITEM: { shadow: { type: 'math_number', fields: { NUM: 0 } } }
            }
          },
          {
            kind: 'block',
            type: 'reto_list_get',
            fields: {
              LIST: { name: latestList.getName(), type: 'list' }
            },
            inputs: {
              INDEX: { shadow: { type: 'math_number', fields: { NUM: 1 } } }
            }
          },
          {
            kind: 'block',
            type: 'reto_list_length',
            fields: {
              LIST: { name: latestList.getName(), type: 'list' }
            }
          },
          {
            kind: 'block',
            type: 'reto_list_contains',
            fields: {
              LIST: { name: latestList.getName(), type: 'list' }
            },
            inputs: {
              ITEM: { shadow: { type: 'math_number', fields: { NUM: 0 } } }
            }
          }
        );
      }

      return xmlList;
    };

    // Đăng ký hai danh mục độc lập
    this.workspace.registerToolboxCategoryCallback('VARIABLE_CATEGORY', variableCategoryCallback);
    this.workspace.registerToolboxCategoryCallback('LIST_CATEGORY', listCategoryCallback);

    // Tương thích ngược với định danh cũ VARIABLE_AND_LIST nếu có
    this.workspace.registerToolboxCategoryCallback('VARIABLE_AND_LIST', (ws) => {
      return [...variableCategoryCallback(ws), ...listCategoryCallback(ws)];
    });

    // 5. Danh mục "Khối của tôi" (CUSTOM_PROCEDURE)
    this.workspace.registerToolboxCategoryCallback('CUSTOM_PROCEDURE', (ws) => {
      const list = [
        {
          kind: 'button',
          text: 'Tạo một khối...',
          callbackKey: 'CREATE_CUSTOM_BLOCK'
        }
      ];

      if (typeof Blockly.Procedures.flyoutCategory === 'function') {
        try {
          const procBlocks = Blockly.Procedures.flyoutCategory(ws);
          // Chuẩn LEGO SPIKE / Scratch: Chỉ hiển thị các khối gọi (caller blocks),
          // khi chưa tạo khối nào thì chỉ hiển thị duy nhất nút [Tạo một khối...]
          const callers = procBlocks.filter(b => b.kind === 'block' && (b.type === 'procedures_callnoreturn' || b.type === 'procedures_callreturn'));
          callers.forEach(c => {
            c.inline = true;
            if (c.extraState && Array.isArray(c.extraState.userCreatedParams)) {
              if (!c.inputs) c.inputs = {};
              c.extraState.userCreatedParams.forEach((param, idx) => {
                const argKey = 'ARG' + idx;
                if (!c.inputs[argKey]) {
                  c.inputs[argKey] = {
                    shadow: {
                      type: 'math_number',
                      fields: { NUM: 0 }
                    }
                  };
                }
              });
            }
          });
          list.push(...callers);
        } catch (e) {
          console.warn('Lỗi hiển thị thủ tục flyout:', e);
        }
      }

      return list;
    });
  }

  getPythonCode() {
    return generateRetoCarPythonCode(this.workspace);
  }

  saveBlocksData() {
    if (!this.workspace) return null;
    return Blockly.serialization.workspaces.save(this.workspace);
  }

  loadBlocksData(data) {
    if (!this.workspace || !data) return;

    try {
      // Nếu dữ liệu theo chuẩn Blockly Serialization JSON mới
      if (data.blocks && Array.isArray(data.blocks.blocks)) {
        Blockly.serialization.workspaces.load(data, this.workspace);
        return;
      }

      // Nếu dữ liệu theo cấu trúc AST cũ (tương thích ngược)
      this.loadDefaultProgram();
    } catch (err) {
      console.warn('Lỗi khi tải blocksData vào Blockly, khôi phục chương trình mặc định:', err);
      this.loadDefaultProgram();
    }
  }

  loadDefaultProgram() {
    if (!this.workspace) return;
    // Để trống hoàn toàn theo yêu cầu người dùng
    this.workspace.clear();
  }

  clear() {
    if (this.workspace) {
      this.workspace.clear();
    }
  }

  dispose() {
    if (this.toolboxObserver) {
      this.toolboxObserver.disconnect();
      this.toolboxObserver = null;
    }
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
      this.resizeHandler = null;
    }
    if (this.workspace) {
      this.workspace.dispose();
      this.workspace = null;
    }
  }
}
